/**
 * Push Notification Service
 * Uses native Web Push API (no Firebase required)
 *
 * Based on https://whatpwacando.today/notifications
 *
 * Requirements:
 * - HTTPS required (or localhost for dev)
 * - PWA must be installed
 * - User must grant notification permission
 */

import { request } from "@/utilities/libs/request";

const VAPID_PUBLIC_KEY_ENV = import.meta.env.VITE_PUSH_VAPID_PUBLIC_KEY || "";

// Check if we're in a secure context (HTTPS or localhost)
const isSecureContext = (): boolean => {
  return (
    window.location.protocol === "https:" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );
};

// Check if app is running as installed PWA (not in browser tab)
const isStandalonePWA = (): boolean => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};

// Check if Push API is supported
export const isPushSupported = (): boolean => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const hasSecureContext = isSecureContext();
  const isPWA = isStandalonePWA();

  // iOS requires HTTPS for Service Workers
  if (isIOS && !hasSecureContext) {
    return false;
  }

  // iOS requires standalone PWA mode for push notifications
  if (isIOS && !isPWA) {
    return false;
  }

  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
};

// Get current notification permission status
export const getNotificationPermission = (): NotificationPermission => {
  return Notification.permission;
};

// Request notification permission from user
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  // Only request permission in secure context to prevent crashes on mobile
  if (!isSecureContext()) {
    console.warn("Notification permission requires HTTPS (or localhost)");
    return "denied";
  }

  const permission = await Notification.requestPermission();
  return permission;
}

// Fetch VAPID public key from backend API
export async function fetchVapidKey(): Promise<string | null> {
  // Try env first
  if (VAPID_PUBLIC_KEY_ENV) {
    return VAPID_PUBLIC_KEY_ENV;
  }

  // Fetch from backend API using request utility (no auth needed for public key)
  try {
    const response = await request<{ data: string }>({
      url: "/push-subscription/vapid-key",
      method: "GET",
    });
    return response.data || null;
  } catch (error) {
    console.error("Failed to fetch VAPID key:", error);
  }

  return null;
}

// Subscribe to push notifications
export async function subscribeToPush(
  registration: ServiceWorkerRegistration
): Promise<PushSubscription | null> {
  const vapidKey = await fetchVapidKey();
  if (!vapidKey) {
    console.warn("VAPID public key not configured");
    return null;
  }

  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    console.log("Push subscribed:", subscription);
    return subscription;
  } catch (error) {
    console.error("Push subscription failed:", error);
    return null;
  }
}

// Get existing push subscription
export async function getExistingSubscription(
  registration: ServiceWorkerRegistration
): Promise<PushSubscription | null> {
  try {
    const subscription = await registration.pushManager.getSubscription();
    return subscription;
  } catch (error) {
    console.error("Get subscription failed:", error);
    return null;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush(
  subscription: PushSubscription
): Promise<boolean> {
  try {
    return await subscription.unsubscribe();
  } catch (error) {
    console.error("Unsubscribe failed:", error);
    return false;
  }
}

// Send subscription to backend for storage
export async function sendSubscriptionToBackend(
  subscription: PushSubscription
): Promise<boolean> {
  try {
    await request({
      url: "/push-subscription",
      method: "POST",
      data: {
        endpoint: subscription.endpoint,
        keys: subscription.toJSON().keys,
        deviceName: getDeviceName(),
        userAgent: navigator.userAgent,
      },
    });
    return true;
  } catch (error) {
    console.error("Failed to send subscription to backend:", error);
    return false;
  }
}

// Get device name based on user agent
function getDeviceName(): string {
  const ua = navigator.userAgent;
  let deviceName = "Unknown Device";

  if (ua.includes("Mac")) {
    deviceName = ua.includes("iPhone") ? "iPhone" : "Mac";
  } else if (ua.includes("Windows")) {
    deviceName = "Windows PC";
  } else if (ua.includes("iPad")) {
    deviceName = "iPad";
  } else if (ua.includes("Linux")) {
    deviceName = "Linux PC";
  } else if (ua.includes("Android")) {
    deviceName = "Android Device";
  }

  // Add browser info
  if (ua.includes("Chrome")) {
    deviceName += " (Chrome)";
  } else if (ua.includes("Safari") && !ua.includes("Chrome")) {
    deviceName += " (Safari)";
  } else if (ua.includes("Firefox")) {
    deviceName += " (Firefox)";
  } else if (ua.includes("Edge")) {
    deviceName += " (Edge)";
  }

  return deviceName;
}

// Remove subscription from backend
export async function removeSubscriptionFromBackend(): Promise<boolean> {
  try {
    await request({
      url: "/push-subscription",
      method: "DELETE",
    });
    return true;
  } catch (error) {
    console.error("Failed to remove subscription from backend:", error);
    return false;
  }
}

// Convert VAPID key to Uint8Array (required for subscribe)
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

// Show a notification (for testing)
export async function showTestNotification(
  title: string = "Test Notification",
  options: NotificationOptions = {}
): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service workers not supported");
    return;
  }

  const registration = await navigator.serviceWorker.ready;

  await registration.showNotification(title, {
    body: options.body || "This is a test notification",
    icon: options.icon || "/icon.png",
    badge: options.badge || "/icon.png",
    tag: options.tag || "test-notification",
    requireInteraction: options.requireInteraction ?? false,
    ...options,
  });
}

// Types for push event data
export interface PushNotificationData {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  url?: string;
  leadId?: string;
  projectId?: string;
  type?: string;
  [key: string]: unknown;
}